/**
 * Job Slice for Wadakaroo
 *
 * Manages state for workshop jobs:
 * - CRUD operations
 * - Job lifecycle (start, pause, resume, complete, cancel)
 * - Staff assignment
 * - Work items management
 * - Notes/comments
 * - Search, filtering, and pagination
 */

import { createSlice, createAsyncThunk, isAnyOf } from '@reduxjs/toolkit';
import { readPage } from '../../utils/pageShape';
import {
    getQueuedJobsApi,
    setJobOnTvApi,
    QUEUED_PAGE_SIZE,
    getJobsApi,
    getJobApi,
    createJobFromBookingApi,
    createStandaloneJobApi,
    updateJobApi,
    updateJobStatusApi,
    startJobApi,
    pauseJobApi,
    resumeJobApi,
    completeJobApi,
    recordOdometerInApi,
    cancelJobApi,
    purgeJobApi,
    vehicleCollectedApi,
    requestJobApprovalApi,
    updateJobPlannerOrderApi,
    assignStaffApi,
    unassignStaffApi,
    addWorkItemApi,
    updateWorkItemApi,
    updateWorkItemStatusApi,
    removeWorkItemApi,
    startAllWorkItemsApi,
    completeAllWorkItemsApi,
    recordObservationApi,
    recordObservationsApi,
    linkMeasurementApi,
    unlinkMeasurementApi,
    listMeasurementsApi,
    addJobNoteApi,
    getJobsByStatusApi,
    getMyJobsApi,
    getJobsByStaffApi,
    getUnassignedJobsApi,
    getInProgressJobsApi,
    getPausedJobsApi,
    getJobsByCustomerApi,
    getJobsByVehicleApi,
    searchJobsApi,
    getJobSummaryApi,
    getJobResourcesApi,
    getAvailableResourcesApi,
    assignResourceApi,
    unassignResourceApi,
    getCategoryTypesApi,
    getWorkCategoriesApi,
    scheduleJobApi,
    markJobVehicleReceivedApi,
} from '../../services/jobApi';

// ============================================
// Async Thunks - Fetch Operations
// ============================================

export const fetchJobs = createAsyncThunk(
    'jobs/fetchAll',
    async ({ page = 0, size = 20, myJobsOnly = false, bustCache = false,
               fromDate = null, toDate = null, includeOpenJobs = false, q = '' } = {}, { rejectWithValue }) => {
        try {
            return await getJobsApi({ page, size, myJobsOnly, bustCache, fromDate, toDate, includeOpenJobs, q });
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch jobs');
        }
    }
);

/**
 * The Queued column, from its own endpoint.
 *
 * 🔑 Separate from `fetchJobs` and from `state.jobs`. The month payload still carries
 * open jobs for the split view and the phone, but the BOARD's Queued column is drawn
 * from this page alone — so a queued job is never on the board twice and the count
 * badge is the count of ALL queued jobs rather than the twenty on screen. The search
 * reaches the server once the typing settles; each view narrows the page locally in
 * between.
 */
export const fetchQueuedJobs = createAsyncThunk(
    'jobs/fetchQueued',
    async ({ q = '', onTv = null, page = 0, size = QUEUED_PAGE_SIZE, bustCache = false } = {},
           { rejectWithValue }) => {
        try {
            return await getQueuedJobsApi({ q, onTv, page, size, bustCache });
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch queued jobs');
        }
    }
);

/** Pick a job for the TV, or take it off. The server answers with the whole job. */
export const setJobOnTv = createAsyncThunk(
    'jobs/setOnTv',
    async ({ uuid, onTv }, { rejectWithValue }) => {
        try {
            return await setJobOnTvApi(uuid, onTv);
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to update the TV pick');
        }
    }
);

export const fetchJob = createAsyncThunk(
    'jobs/fetchOne',
    async (uuid, { rejectWithValue }) => {
        try {
            return await getJobApi(uuid);
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch job');
        }
    }
);

export const fetchJobsByStatus = createAsyncThunk(
    'jobs/fetchByStatus',
    async (status, { rejectWithValue }) => {
        try {
            return await getJobsByStatusApi(status);
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch jobs by status');
        }
    }
);

export const fetchMyJobs = createAsyncThunk(
    'jobs/fetchMyJobs',
    async (_, { rejectWithValue }) => {
        try {
            return await getMyJobsApi();
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch my jobs');
        }
    }
);

export const fetchJobsByStaff = createAsyncThunk(
    'jobs/fetchByStaff',
    async (staffUuid, { rejectWithValue }) => {
        try {
            return await getJobsByStaffApi(staffUuid);
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch jobs for staff');
        }
    }
);

export const fetchUnassignedJobs = createAsyncThunk(
    'jobs/fetchUnassigned',
    async (_, { rejectWithValue }) => {
        try {
            return await getUnassignedJobsApi();
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch unassigned jobs');
        }
    }
);

export const fetchInProgressJobs = createAsyncThunk(
    'jobs/fetchInProgress',
    async (_, { rejectWithValue }) => {
        try {
            return await getInProgressJobsApi();
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch in-progress jobs');
        }
    }
);

export const fetchPausedJobs = createAsyncThunk(
    'jobs/fetchPaused',
    async (_, { rejectWithValue }) => {
        try {
            return await getPausedJobsApi();
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch paused jobs');
        }
    }
);

export const fetchJobsByCustomer = createAsyncThunk(
    'jobs/fetchByCustomer',
    async (customerUuid, { rejectWithValue }) => {
        try {
            return await getJobsByCustomerApi(customerUuid);
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch jobs for customer');
        }
    }
);

export const fetchJobsByVehicle = createAsyncThunk(
    'jobs/fetchByVehicle',
    async (vehicleUuid, { rejectWithValue }) => {
        try {
            return await getJobsByVehicleApi(vehicleUuid);
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch jobs for vehicle');
        }
    }
);

export const searchJobs = createAsyncThunk(
    'jobs/search',
    async ({ query, page = 0, size = 20 }, { rejectWithValue }) => {
        try {
            return await searchJobsApi(query, { page, size });
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to search jobs');
        }
    }
);

export const fetchJobSummary = createAsyncThunk(
    'jobs/fetchSummary',
    async (_, { rejectWithValue }) => {
        try {
            return await getJobSummaryApi();
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch job summary');
        }
    }
);

export const fetchCategoryTypes = createAsyncThunk(
    'jobs/fetchCategoryTypes',
    async (_, { rejectWithValue }) => {
        try {
            return await getCategoryTypesApi();
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch category types');
        }
    }
);

export const scheduleJob = createAsyncThunk(
    'jobs/schedule',
    async ({ jobUuid, scheduledDate, scheduledTime }, { rejectWithValue }) => {
        try {
            return await scheduleJobApi(jobUuid, { scheduledDate, scheduledTime });
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to book the job in');
        }
    }
);

export const markJobVehicleReceived = createAsyncThunk(
    'jobs/markVehicleReceived',
    async ({ jobUuid, loanCarDetails = null }, { rejectWithValue }) => {
        try {
            return await markJobVehicleReceivedApi(jobUuid, loanCarDetails);
        } catch (error) {
            // The server names what is missing — a customer with no email, a loan car
            // still out — and a generic message would throw that away.
            return rejectWithValue(error.response?.data?.message || 'Failed to mark the vehicle as received');
        }
    }
);

export const fetchWorkCategories = createAsyncThunk(
    'jobs/fetchWorkCategories',
    async (_, { rejectWithValue }) => {
        try {
            return await getWorkCategoriesApi();
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch work categories');
        }
    }
);

// ============================================
// Async Thunks - CRUD Operations
// ============================================

export const createJobFromBooking = createAsyncThunk(
    'jobs/createFromBooking',
    async (data, { rejectWithValue }) => {
        try {
            return await createJobFromBookingApi(data);
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to create job from booking');
        }
    }
);

export const createStandaloneJob = createAsyncThunk(
    'jobs/createStandalone',
    async (data, { rejectWithValue }) => {
        try {
            return await createStandaloneJobApi(data);
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to create job');
        }
    }
);

export const updateJob = createAsyncThunk(
    'jobs/update',
    async ({ uuid, data }, { rejectWithValue }) => {
        try {
            return await updateJobApi(uuid, data);
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to update job');
        }
    }
);

export const updateJobPlannerOrder = createAsyncThunk(
    'jobs/updatePlannerOrder',
    async ({ uuid, plannerOrder }, { rejectWithValue }) => {
        try {
            return await updateJobPlannerOrderApi(uuid, plannerOrder);
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to update planner order');
        }
    }
);

// ============================================
// Async Thunks - Job Lifecycle
// ============================================

export const updateJobStatus = createAsyncThunk(
    'jobs/updateStatus',
    async ({ uuid, status }, { rejectWithValue }) => {
        try {
            return await updateJobStatusApi(uuid, status);
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to update job status');
        }
    }
);

export const startJob = createAsyncThunk(
    'jobs/start',
    async (uuid, { rejectWithValue }) => {
        try {
            return await startJobApi(uuid);
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to start job');
        }
    }
);

export const pauseJob = createAsyncThunk(
    'jobs/pause',
    async ({ uuid, reason }, { rejectWithValue }) => {
        try {
            return await pauseJobApi(uuid, reason);
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to pause job');
        }
    }
);

export const resumeJob = createAsyncThunk(
    'jobs/resume',
    async (uuid, { rejectWithValue }) => {
        try {
            return await resumeJobApi(uuid);
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to resume job');
        }
    }
);

export const completeJob = createAsyncThunk(
    'jobs/complete',
    async ({ uuid, odometerOut }, { rejectWithValue }) => {
        try {
            return await completeJobApi(uuid, odometerOut);
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to complete job');
        }
    }
);

/**
 * Record the vehicle-in odometer reading. Standalone single-field operation
 * usable across every job status (the technician may capture or correct the
 * reading at any point in the lifecycle). Backed by PATCH /jobs/{uuid}/odometer-in.
 */
export const recordOdometerIn = createAsyncThunk(
    'jobs/recordOdometerIn',
    async ({ uuid, odometerIn }, { rejectWithValue }) => {
        try {
            return await recordOdometerInApi(uuid, odometerIn);
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to record odometer reading');
        }
    }
);

export const cancelJob = createAsyncThunk(
    'jobs/cancel',
    async ({ uuid, reason }, { rejectWithValue }) => {
        try {
            return await cancelJobApi(uuid, reason);
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to cancel job');
        }
    }
);

/**
 * Hard-delete (purge) a job and its linked booking + invoice.
 *
 * Irreversible — backed by DELETE /api/jobs/{uuid}/purge. Returns the uuid
 * on success so reducer cases can remove it from `state.jobs`.
 */
export const purgeJob = createAsyncThunk(
    'jobs/purge',
    async (uuid, { rejectWithValue }) => {
        try {
            return await purgeJobApi(uuid);
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to delete job');
        }
    }
);

/**
 * @param arg `{ uuid, nextServiceDue, nextServiceKm }` — the last two optional.
 *
 * ⚠️ An object, not a bare uuid. Every caller passes `{ uuid }` at minimum; the tablet
 * view sends nothing more, which collects the vehicle exactly as it always did.
 */
export const vehicleCollected = createAsyncThunk(
    'jobs/vehicleCollected',
    async ({ uuid, nextServiceDue = null, nextServiceKm = null }, { rejectWithValue }) => {
        try {
            return await vehicleCollectedApi(uuid, { nextServiceDue, nextServiceKm });
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to mark vehicle as collected');
        }
    }
);

/**
 * Request job-level customer approval (PROFILE approval mode).
 * Re-fetches the full job after the request so selectedJob stays in sync.
 */
export const requestJobApproval = createAsyncThunk(
    'jobs/requestJobApproval',
    async ({ jobUuid, description }, { rejectWithValue }) => {
        try {
            return await requestJobApprovalApi(jobUuid, description);
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to send approval request');
        }
    }
);

// ============================================
// Async Thunks - Staff Assignment
// ============================================

export const assignStaff = createAsyncThunk(
    'jobs/assignStaff',
    async ({ uuid, staffUuid }, { rejectWithValue }) => {
        try {
            return await assignStaffApi(uuid, staffUuid);
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to assign staff');
        }
    }
);

export const unassignStaff = createAsyncThunk(
    'jobs/unassignStaff',
    async (uuid, { rejectWithValue }) => {
        try {
            return await unassignStaffApi(uuid);
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to unassign staff');
        }
    }
);

// ============================================
// Async Thunks - Work Item Operations
// ============================================

export const addWorkItem = createAsyncThunk(
    'jobs/addWorkItem',
    async ({ jobUuid, data }, { rejectWithValue }) => {
        try {
            return await addWorkItemApi(jobUuid, data);
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to add work item');
        }
    }
);

export const updateWorkItem = createAsyncThunk(
    'jobs/updateWorkItem',
    async ({ jobUuid, workItemUuid, data }, { rejectWithValue }) => {
        try {
            return await updateWorkItemApi(jobUuid, workItemUuid, data);
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to update work item');
        }
    }
);

export const updateWorkItemStatus = createAsyncThunk(
    'jobs/updateWorkItemStatus',
    async ({ jobUuid, workItemUuid, status }, { rejectWithValue }) => {
        try {
            return await updateWorkItemStatusApi(jobUuid, workItemUuid, status);
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to update work item status');
        }
    }
);

export const removeWorkItem = createAsyncThunk(
    'jobs/removeWorkItem',
    async ({ jobUuid, workItemUuid }, { rejectWithValue }) => {
        try {
            return await removeWorkItemApi(jobUuid, workItemUuid);
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to remove work item');
        }
    }
);

/**
 * Bulk-start every PENDING work item on a job.
 * One server-side transaction — returns the full updated job detail response
 * which is merged into state via the standard fulfilled-case pattern.
 */
export const startAllWorkItems = createAsyncThunk(
    'jobs/startAllWorkItems',
    async ({ jobUuid }, { rejectWithValue }) => {
        try {
            return await startAllWorkItemsApi(jobUuid);
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to start work items');
        }
    }
);

/**
 * Bulk-complete every non-terminal work item on a job.
 * For profile-seeded items the OUT measurement list is auto-filled with the
 * green/OK value server-side. AWAITING_CONFIRMATION items are skipped server-side.
 * Returns the full updated job detail response.
 */
export const completeAllWorkItems = createAsyncThunk(
    'jobs/completeAllWorkItems',
    async ({ jobUuid }, { rejectWithValue }) => {
        try {
            return await completeAllWorkItemsApi(jobUuid);
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to complete work items');
        }
    }
);

// ============================================
// Async Thunks - Measurement Observations
// ============================================

/**
 * Record (upsert) a measurement observation on a work item.
 * Saves silently — no global spinner so the technician can keep typing.
 * On success, the full updated job is merged into state so work item
 * cards re-render with the new observationStatus colour immediately.
 *
 * For multi-position measurements (e.g. tyres LF/RF/LR/RR), pass
 * positionLabel to identify which specific slot is being recorded.
 * For measurements with sub-positions (e.g. tyre tread OUTER/MIDDLE/INNER)
 * also pass subPositionLabel to identify which cell within that position.
 * For single-slot measurements both labels are null/omitted.
 */
export const recordObservation = createAsyncThunk(
    'jobs/recordObservation',
    async ({
               jobUuid, workItemUuid, measurementUuid, value,
               positionLabel = null, subPositionLabel = null, phase = 'IN'
           }, { rejectWithValue }) => {
        try {
            // The observations endpoint returns List<ObservationResponse>, not a full job.
            // Discard that and re-fetch the full job so the reducer always receives a
            // valid JobDetailResponse — prevents selectedJob being replaced with a bare array.
            //
            // The phase param routes the write into either the IN (pre-work/baseline) or
            // OUT (post-work) observation list on the backend's JobWorkItem entity.
            await recordObservationApi(
                jobUuid, workItemUuid, measurementUuid, value,
                positionLabel, subPositionLabel, phase
            );
            return await getJobApi(jobUuid);
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to save observation');
        }
    }
);

/**
 * Record several observations at once — see recordObservationsApi for why this is a
 * correctness fix rather than a round-trip saving.
 *
 * Re-fetches the job for the same reason the single-value thunk does: the endpoint
 * returns a list of observations, and the reducer needs a full JobDetailResponse.
 */
export const recordObservations = createAsyncThunk(
    'jobs/recordObservations',
    async ({ jobUuid, workItemUuid, observations, phase = 'IN' }, { rejectWithValue }) => {
        try {
            await recordObservationsApi(jobUuid, workItemUuid, observations, phase);
            return await getJobApi(jobUuid);
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to save observations');
        }
    }
);

/**
 * Link a measurement to an ad-hoc work item — creates a blank placeholder
 * observation so the UI can render the input row before the technician records
 * a value. Re-fetches the full job so selectedJob stays in sync with the work
 * item's updated measurementObservations array.
 *
 * Backend rejects this for profile-seeded items (their measurement set is fixed).
 */
export const linkMeasurement = createAsyncThunk(
    'jobs/linkMeasurement',
    async ({ jobUuid, workItemUuid, measurementUuid }, { rejectWithValue }) => {
        try {
            await linkMeasurementApi(jobUuid, workItemUuid, measurementUuid);
            return await getJobApi(jobUuid);
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to link measurement');
        }
    }
);

/**
 * Unlink a measurement observation from an ad-hoc work item.
 * Backend rejects this for profile-seeded items.
 */
export const unlinkMeasurement = createAsyncThunk(
    'jobs/unlinkMeasurement',
    async ({ jobUuid, workItemUuid, measurementUuid }, { rejectWithValue }) => {
        try {
            await unlinkMeasurementApi(jobUuid, workItemUuid, measurementUuid);
            return await getJobApi(jobUuid);
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to unlink measurement');
        }
    }
);

/**
 * Fetch all measurements visible to the current organisation
 * (org-owned + global). Used by the "+ Add measurement" picker on
 * ad-hoc work items. Cached in state — fetch once per session unless
 * the picker is opened with bustCache=true.
 */
export const fetchAvailableMeasurements = createAsyncThunk(
    'jobs/fetchAvailableMeasurements',
    async ({ activeOnly = true, page = 0, size = 200 } = {}, { rejectWithValue }) => {
        try {
            const result = await listMeasurementsApi({ activeOnly, page, size });
            // Backend returns a Page<MeasurementResponse> — extract the content array.
            return result?.content || result || [];
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to load measurements');
        }
    }
);

// ============================================
// Async Thunks - Resource Allocation
// ============================================

export const fetchJobResources = createAsyncThunk(
    'jobs/fetchResources',
    async (jobUuid, { rejectWithValue }) => {
        try {
            return await getJobResourcesApi(jobUuid);
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch job resources');
        }
    }
);

export const fetchAvailableResources = createAsyncThunk(
    'jobs/fetchAvailableResources',
    async ({ jobUuid, type = null }, { rejectWithValue }) => {
        try {
            return await getAvailableResourcesApi(jobUuid, type);
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch available resources');
        }
    }
);

export const assignResource = createAsyncThunk(
    'jobs/assignResource',
    async ({ jobUuid, data }, { dispatch, rejectWithValue }) => {
        try {
            await assignResourceApi(jobUuid, data);
            // Re-fetch both lists so state is always consistent with server
            const [resources, available] = await Promise.all([
                dispatch(fetchJobResources(jobUuid)).unwrap(),
                dispatch(fetchAvailableResources({ jobUuid })).unwrap(),
            ]);
            return { resources, available };
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to assign resource');
        }
    }
);

export const unassignResource = createAsyncThunk(
    'jobs/unassignResource',
    async ({ jobUuid, resourceUuid }, { dispatch, rejectWithValue }) => {
        try {
            await unassignResourceApi(jobUuid, resourceUuid);
            // Re-fetch both lists so state is always consistent with server
            const [resources, available] = await Promise.all([
                dispatch(fetchJobResources(jobUuid)).unwrap(),
                dispatch(fetchAvailableResources({ jobUuid })).unwrap(),
            ]);
            return { resources, available };
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to release resource');
        }
    }
);

// ============================================
// Async Thunks - Note Operations
// ============================================

export const addJobNote = createAsyncThunk(
    'jobs/addNote',
    async ({ jobUuid, data }, { rejectWithValue }) => {
        try {
            return await addJobNoteApi(jobUuid, data);
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to add note');
        }
    }
);


// ============================================
// Initial State
// ============================================

const initialState = {
    // Job data
    jobs: [],
    selectedJob: null,
    myJobs: [],
    unassignedJobs: [],
    inProgressJobs: [],
    pausedJobs: [],

    // Summary counts
    summary: {
        created: 0,
        quoted: 0,
        approved: 0,
        inProgress: 0,
        paused: 0,
        completed: 0,
        invoiced: 0,
        cancelled: 0,
        vehicleCollected: 0,
        active: 0,
    },

    /**
     * The Queued column — its own list and its own page, never merged into `jobs`.
     * `total` is the server's totalSize: how many queued jobs there ARE, which is
     * what the column's badge shows, not how many are on this page.
     */
    queued: {
        jobs: [],
        total: 0,
        page: 0,
        totalPages: 0,
        loading: false,
        error: null,
    },

    // Pagination
    pagination: {
        page: 0,
        size: 20,
        totalElements: 0,
        totalPages: 0,
    },

    // Filters
    statusFilter: 'all',
    searchQuery: '',
    staffFilter: null,

    // Loading states
    jobsLoading: false,
    jobsError: null,

    // Operation states
    operationLoading: false,
    /** The open job is being fetched — see the fetchJob cases. */
    detailLoading: false,
    operationError: null,
    operationSuccess: null,

    // UI State - Modals
    showCreateModal: false,
    showCreateFromBookingModal: false,
    showEditModal: false,
    showViewModal: false,
    showAssignModal: false,
    showWorkItemModal: false,
    showEditWorkItemModal: false,
    editingWorkItem: null,
    showNoteModal: false,
    showCancelConfirm: false,
    showDeleteConfirm: false,
    showCompleteConfirm: false,
    showVehicleCollectedConfirm: false,

    // Loan car return modal — shown after vehicle collected when booking had a loan car
    showLoanCarReturnModal: false,
    loanCarReturnJob: null,   // { uuid, loanCarRego }

    // Enum metadata (populated once on mount from /meta endpoints)
    categoryTypes: [],
    workCategories: [],
    metaLoading: false,

    // Resource allocation state
    jobResources: [],
    availableResources: [],
    resourcesLoading: false,
    resourcesError: null,

    // Measurements catalogue (org-owned + global) — used by the
    // "+ Add measurement" picker on ad-hoc work items. Populated lazily.
    availableMeasurements: [],
    availableMeasurementsLoading: false,
    availableMeasurementsLoaded: false,
    availableMeasurementsError: null,
};

// ============================================
// Slice
// ============================================

const jobSlice = createSlice({
    name: 'jobs',
    initialState,
    reducers: {
        // Filter actions
        setStatusFilter: (state, action) => {
            state.statusFilter = action.payload;
            state.pagination.page = 0;
        },
        setSearchQuery: (state, action) => {
            state.searchQuery = action.payload;
        },
        setStaffFilter: (state, action) => {
            state.staffFilter = action.payload;
            state.pagination.page = 0;
        },
        clearFilters: (state) => {
            state.statusFilter = 'all';
            state.searchQuery = '';
            state.staffFilter = null;
            state.pagination.page = 0;
        },

        // Pagination actions
        setPage: (state, action) => {
            state.pagination.page = action.payload;
        },
        setPageSize: (state, action) => {
            state.pagination.size = action.payload;
            state.pagination.page = 0;
        },

        // Modal actions - Create
        openCreateModal: (state) => {
            state.showCreateModal = true;
        },
        closeCreateModal: (state) => {
            state.showCreateModal = false;
        },
        openCreateFromBookingModal: (state) => {
            state.showCreateFromBookingModal = true;
        },
        closeCreateFromBookingModal: (state) => {
            state.showCreateFromBookingModal = false;
        },

        // Modal actions - Edit/View
        openEditModal: (state, action) => {
            state.selectedJob = action.payload;
            state.showEditModal = true;
        },
        closeEditModal: (state) => {
            state.showEditModal = false;
        },
        openViewModal: (state, action) => {
            state.selectedJob = action.payload;
            state.showViewModal = true;
        },
        closeViewModal: (state) => {
            state.showViewModal = false;
        },

        // Modal actions - Assignment
        openAssignModal: (state, action) => {
            state.selectedJob = action.payload;
            state.showAssignModal = true;
        },
        closeAssignModal: (state) => {
            state.showAssignModal = false;
        },

        // Modal actions - Work Items
        openWorkItemModal: (state, action) => {
            state.selectedJob = action.payload;
            state.showWorkItemModal = true;
        },
        closeWorkItemModal: (state) => {
            state.showWorkItemModal = false;
        },

        // Modal actions - Edit Work Item
        openEditWorkItemModal: (state, action) => {
            state.selectedJob = action.payload.job;
            state.editingWorkItem = action.payload.workItem;
            state.showEditWorkItemModal = true;
        },
        closeEditWorkItemModal: (state) => {
            state.showEditWorkItemModal = false;
            state.editingWorkItem = null;
        },

        // Modal actions - Notes
        openNoteModal: (state, action) => {
            state.selectedJob = action.payload;
            state.showNoteModal = true;
        },
        closeNoteModal: (state) => {
            state.showNoteModal = false;
        },

        // Modal actions - Confirm dialogs
        openCancelConfirm: (state, action) => {
            state.selectedJob = action.payload;
            state.showCancelConfirm = true;
        },
        closeCancelConfirm: (state) => {
            state.showCancelConfirm = false;
        },
        openDeleteConfirm: (state, action) => {
            state.selectedJob = action.payload;
            state.showDeleteConfirm = true;
            state.operationError = null;
        },
        closeDeleteConfirm: (state) => {
            state.showDeleteConfirm = false;
            state.operationError = null;
        },
        openCompleteConfirm: (state, action) => {
            state.selectedJob = action.payload;
            state.showCompleteConfirm = true;
        },
        closeCompleteConfirm: (state) => {
            state.showCompleteConfirm = false;
            state.operationError = null;
        },
        openVehicleCollectedConfirm: (state, action) => {
            state.selectedJob = action.payload;
            state.showVehicleCollectedConfirm = true;
        },
        closeVehicleCollectedConfirm: (state) => {
            state.showVehicleCollectedConfirm = false;
        },

        // Loan car return modal
        openLoanCarReturnModal: (state, action) => {
            // action.payload = { uuid, loanCarRego }
            state.showLoanCarReturnModal = true;
            state.loanCarReturnJob = action.payload;
        },
        closeLoanCarReturnModal: (state) => {
            state.showLoanCarReturnModal = false;
            state.loanCarReturnJob = null;
        },

        // Selection
        selectJob: (state, action) => {
            state.selectedJob = action.payload;
        },
        clearSelectedJob: (state) => {
            state.selectedJob = null;
        },

        // Operation status
        clearOperationStatus: (state) => {
            state.operationError = null;
            state.operationSuccess = null;
        },
        setOperationSuccess: (state, action) => {
            state.operationSuccess = action.payload;
        },
    },

    extraReducers: (builder) => {
        builder
            // ==================== Fetch Jobs ====================
            .addCase(fetchJobs.pending, (state) => {
                state.jobsLoading = true;
                state.jobsError = null;
            })
            .addCase(fetchJobs.fulfilled, (state, action) => {
                state.jobsLoading = false;
                state.jobs = action.payload.content || action.payload;
                // Either spelling of a page: the older shape says totalElements, a
                // Micronaut Page says totalSize. The phone's all-jobs pager reads these.
                const total = action.payload.totalElements ?? action.payload.totalSize;
                if (total !== undefined) {
                    // Same reader as the queued page — see utils/pageShape for why
                    // totalPages cannot be read straight off the response.
                    const read = readPage(action.payload, {
                        size: action.meta?.arg?.size ?? 20,
                        page: action.meta?.arg?.page ?? state.pagination.page,
                    });
                    state.pagination.totalElements = read.total;
                    state.pagination.totalPages = read.totalPages;
                    state.pagination.page = read.page;
                }
            })
            .addCase(fetchJobs.rejected, (state, action) => {
                state.jobsLoading = false;
                state.jobsError = action.payload;
            })

            // ==================== Queued column ====================
            .addCase(fetchQueuedJobs.pending, (state) => {
                state.queued.loading = true;
                state.queued.error = null;
            })
            .addCase(fetchQueuedJobs.fulfilled, (state, action) => {
                // 🪤 Read through readPage — a Micronaut Page sends no totalPages or
                // pageNumber, so both are derived there. See utils/pageShape.
                const { content, total, page, totalPages } = readPage(action.payload, {
                    size: action.meta?.arg?.size ?? QUEUED_PAGE_SIZE,
                    page: action.meta?.arg?.page ?? 0,
                });
                state.queued.loading = false;
                state.queued.jobs = content;
                state.queued.total = total;
                state.queued.page = page;
                state.queued.totalPages = totalPages;
            })
            .addCase(fetchQueuedJobs.rejected, (state, action) => {
                state.queued.loading = false;
                state.queued.error = action.payload;
            })

            // ==================== TV pick ====================
            .addCase(setJobOnTv.fulfilled, (state, action) => {
                replaceEverywhere(state, action.payload);
            })
            .addCase(setJobOnTv.rejected, (state, action) => {
                state.operationError = action.payload;
            })

            // ==================== Fetch Job Summary ====================
            .addCase(fetchJobSummary.fulfilled, (state, action) => {
                state.summary = action.payload;
            })

            // ==================== Fetch Enum Metadata ====================
            .addCase(fetchCategoryTypes.pending, (state) => {
                state.metaLoading = true;
            })
            .addCase(fetchCategoryTypes.fulfilled, (state, action) => {
                state.metaLoading = false;
                state.categoryTypes = action.payload;
            })
            .addCase(fetchCategoryTypes.rejected, (state) => {
                state.metaLoading = false;
            })
            .addCase(fetchWorkCategories.pending, (state) => {
                state.metaLoading = true;
            })
            .addCase(fetchWorkCategories.fulfilled, (state, action) => {
                state.metaLoading = false;
                state.workCategories = action.payload;
            })
            .addCase(fetchWorkCategories.rejected, (state) => {
                state.metaLoading = false;
            })

            // ==================== Fetch My Jobs ====================
            .addCase(fetchMyJobs.pending, (state) => {
                state.jobsLoading = true;
            })
            .addCase(fetchMyJobs.fulfilled, (state, action) => {
                state.jobsLoading = false;
                state.myJobs = action.payload;
            })
            .addCase(fetchMyJobs.rejected, (state, action) => {
                state.jobsLoading = false;
                state.jobsError = action.payload;
            })

            // ==================== Fetch Unassigned Jobs ====================
            .addCase(fetchUnassignedJobs.fulfilled, (state, action) => {
                state.unassignedJobs = action.payload;
            })

            // ==================== Fetch In Progress Jobs ====================
            .addCase(fetchInProgressJobs.fulfilled, (state, action) => {
                state.inProgressJobs = action.payload;
            })

            // ==================== Fetch Paused Jobs ====================
            .addCase(fetchPausedJobs.fulfilled, (state, action) => {
                state.pausedJobs = action.payload;
            })

            // ==================== Fetch Single Job ====================
            // 🔑 `detailLoading` as well as `operationLoading`, and not instead of it.
            // operationLoading is every write on the open job — saving a work item,
            // recording a reading — and the card's skeleton must not flash on those.
            // This one means "the job itself is on its way", which is the only time
            // there is nothing to show.
            .addCase(fetchJob.pending, (state) => {
                state.operationLoading = true;
                state.detailLoading = true;
            })
            .addCase(fetchJob.fulfilled, (state, action) => {
                state.operationLoading = false;
                state.detailLoading = false;
                state.selectedJob = action.payload;
            })
            .addCase(fetchJob.rejected, (state, action) => {
                state.operationLoading = false;
                state.detailLoading = false;
                state.operationError = action.payload;
            })

            // ==================== Create Job From Booking ====================
            .addCase(createJobFromBooking.pending, (state) => {
                state.operationLoading = true;
                state.operationError = null;
            })
            .addCase(createJobFromBooking.fulfilled, (state, action) => {
                state.operationLoading = false;
                state.jobs.unshift(action.payload);
                state.showCreateFromBookingModal = false;
                state.operationSuccess = 'Job created successfully';
            })
            .addCase(createJobFromBooking.rejected, (state, action) => {
                state.operationLoading = false;
                state.operationError = action.payload;
            })

            // ==================== Create Standalone Job ====================
            .addCase(createStandaloneJob.pending, (state) => {
                state.operationLoading = true;
                state.operationError = null;
            })
            .addCase(createStandaloneJob.fulfilled, (state, action) => {
                state.operationLoading = false;
                state.jobs.unshift(action.payload);
                state.showCreateModal = false;
                state.operationSuccess = 'Job created successfully';
            })
            .addCase(createStandaloneJob.rejected, (state, action) => {
                state.operationLoading = false;
                state.operationError = action.payload;
            })

            // ==================== Update Job ====================
            .addCase(updateJob.pending, (state) => {
                state.operationLoading = true;
                state.operationError = null;
            })
            .addCase(updateJob.fulfilled, (state, action) => {
                state.operationLoading = false;
                const index = state.jobs.findIndex(j => j.uuid === action.payload.uuid);
                if (index !== -1) {
                    state.jobs[index] = action.payload;
                }
                state.showEditModal = false;
                state.selectedJob = null;
                state.operationSuccess = 'Job updated successfully';
            })
            .addCase(updateJob.rejected, (state, action) => {
                state.operationLoading = false;
                state.operationError = action.payload;
            })

            // ==================== Update Planner Order ====================
            .addCase(updateJobPlannerOrder.fulfilled, (state, action) => {
                const updated = action.payload;
                const index = state.jobs.findIndex(j => j.uuid === updated.uuid);
                if (index !== -1) {
                    state.jobs[index] = { ...state.jobs[index], plannerOrder: updated.plannerOrder };
                }
            })

            // ==================== Update Job Status ====================
            // Generic status setter — used by the kanban drag-and-drop and the
            // status dropdown in JobDetailModal's header. The dedicated lifecycle
            // thunks (startJob, pauseJob, resumeJob, completeJob) remain the
            // preferred path for the happy flow because they also stamp
            // timestamps and run side effects; this thunk is the "manual override"
            // for corrections (e.g. walking a completed job back to in-progress).
            // Mirrors the same fulfilled-handler shape so the kanban + modal
            // update consistently with other lifecycle actions.
            .addCase(updateJobStatus.pending, (state) => {
                state.operationLoading = true;
                state.operationError = null;
            })
            .addCase(updateJobStatus.fulfilled, (state, action) => {
                state.operationLoading = false;
                const index = state.jobs.findIndex(j => j.uuid === action.payload.uuid);
                if (index !== -1) {
                    state.jobs[index] = action.payload;
                }
                if (state.selectedJob?.uuid === action.payload.uuid) {
                    state.selectedJob = action.payload;
                }
                state.operationSuccess = 'Job status updated';
            })
            .addCase(updateJobStatus.rejected, (state, action) => {
                state.operationLoading = false;
                state.operationError = action.payload;
            })

            // ==================== Start Job ====================
            .addCase(startJob.pending, (state) => {
                state.operationLoading = true;
                state.operationError = null;
            })
            .addCase(startJob.fulfilled, (state, action) => {
                state.operationLoading = false;
                const index = state.jobs.findIndex(j => j.uuid === action.payload.uuid);
                if (index !== -1) {
                    state.jobs[index] = action.payload;
                }
                state.operationSuccess = 'Job started';
            })
            .addCase(startJob.rejected, (state, action) => {
                state.operationLoading = false;
                state.operationError = action.payload;
            })

            // ==================== Pause Job ====================
            .addCase(pauseJob.pending, (state) => {
                state.operationLoading = true;
                state.operationError = null;
            })
            .addCase(pauseJob.fulfilled, (state, action) => {
                state.operationLoading = false;
                const index = state.jobs.findIndex(j => j.uuid === action.payload.uuid);
                if (index !== -1) {
                    state.jobs[index] = action.payload;
                }
                state.operationSuccess = 'Job paused';
            })
            .addCase(pauseJob.rejected, (state, action) => {
                state.operationLoading = false;
                state.operationError = action.payload;
            })

            // ==================== Resume Job ====================
            .addCase(resumeJob.pending, (state) => {
                state.operationLoading = true;
                state.operationError = null;
            })
            .addCase(resumeJob.fulfilled, (state, action) => {
                state.operationLoading = false;
                const index = state.jobs.findIndex(j => j.uuid === action.payload.uuid);
                if (index !== -1) {
                    state.jobs[index] = action.payload;
                }
                state.operationSuccess = 'Job resumed';
            })
            .addCase(resumeJob.rejected, (state, action) => {
                state.operationLoading = false;
                state.operationError = action.payload;
            })

            // ==================== Complete Job ====================
            .addCase(completeJob.pending, (state) => {
                state.operationLoading = true;
                state.operationError = null;
            })
            .addCase(completeJob.fulfilled, (state, action) => {
                state.operationLoading = false;
                const index = state.jobs.findIndex(j => j.uuid === action.payload.uuid);
                if (index !== -1) {
                    state.jobs[index] = action.payload;
                }
                state.showCompleteConfirm = false;
                state.selectedJob = null;
                state.operationSuccess = 'Job completed';
            })
            .addCase(completeJob.rejected, (state, action) => {
                state.operationLoading = false;
                state.operationError = action.payload;
            })

            // ==================== Cancel Job ====================
            .addCase(cancelJob.pending, (state) => {
                state.operationLoading = true;
                state.operationError = null;
            })
            .addCase(cancelJob.fulfilled, (state, action) => {
                state.operationLoading = false;
                const index = state.jobs.findIndex(j => j.uuid === action.payload.uuid);
                if (index !== -1) {
                    state.jobs[index] = action.payload;
                }
                state.showCancelConfirm = false;
                state.selectedJob = null;
                state.operationSuccess = 'Job cancelled';
            })
            .addCase(cancelJob.rejected, (state, action) => {
                state.operationLoading = false;
                state.operationError = action.payload;
            })

            // ==================== Purge (hard delete) Job ====================
            // Removes the job row entirely (along with booking + invoice on the
            // backend). Unlike cancel, the job disappears from state.jobs and
            // every open detail modal is closed so no stale view remains.
            .addCase(purgeJob.pending, (state) => {
                state.operationLoading = true;
                state.operationError = null;
            })
            .addCase(purgeJob.fulfilled, (state, action) => {
                state.operationLoading = false;
                state.jobs = state.jobs.filter(j => j.uuid !== action.payload.uuid);
                state.showDeleteConfirm = false;
                state.showViewModal = false;
                state.selectedJob = null;
                state.operationSuccess = 'Job deleted';
            })
            .addCase(purgeJob.rejected, (state, action) => {
                state.operationLoading = false;
                state.operationError = action.payload;
            })

            // ==================== Vehicle Collected ====================
            .addCase(vehicleCollected.pending, (state) => {
                state.operationLoading = true;
                state.operationError = null;
            })
            .addCase(vehicleCollected.fulfilled, (state, action) => {
                state.operationLoading = false;
                const index = state.jobs.findIndex(j => j.uuid === action.payload.uuid);
                if (index !== -1) {
                    state.jobs[index] = action.payload;
                }
                // Always close the confirmation modal and detail view
                state.showVehicleCollectedConfirm = false;
                state.showViewModal = false;
                state.selectedJob = null;

                // If the job's booking had a loan car, open the return modal
                // before firing the success toast so staff complete the IN handover.
                if (action.payload.loanCarRego) {
                    state.showLoanCarReturnModal = true;
                    state.loanCarReturnJob = {
                        uuid: action.payload.uuid,
                        loanCarRego: action.payload.loanCarRego,
                    };
                    // Toast is deferred — fired by JobsPage after the modal is dismissed
                } else {
                    state.operationSuccess = 'Vehicle collected — booking marked complete';
                }
            })
            .addCase(vehicleCollected.rejected, (state, action) => {
                state.operationLoading = false;
                state.operationError = action.payload;
            })

            // ==================== Record Odometer In ====================
            // Single-field write. Backend returns the full JobDetailResponse so
            // the modal stays in sync (matches the rehydrate pattern used by
            // updateJobStatus / vehicleCollected). No modal close, no toast —
            // the inline tile already shows the new value as instant feedback,
            // and a toast for every keystroke-save would be noisy.
            .addCase(recordOdometerIn.pending, (state) => {
                state.operationLoading = true;
                state.operationError = null;
            })
            .addCase(recordOdometerIn.fulfilled, (state, action) => {
                state.operationLoading = false;
                const index = state.jobs.findIndex(j => j.uuid === action.payload.uuid);
                if (index !== -1) {
                    state.jobs[index] = action.payload;
                }
                if (state.selectedJob?.uuid === action.payload.uuid) {
                    state.selectedJob = action.payload;
                }
            })
            .addCase(recordOdometerIn.rejected, (state, action) => {
                state.operationLoading = false;
                state.operationError = action.payload;
            })

            // ==================== Book in / Vehicle received (from the job card) ====================
            // ⚠️ Both endpoints return a JobResponse (the LIST shape), not a
            // JobDetailResponse — so selectedJob is MERGED, never replaced. Replacing it
            // would blank the work items, notes and measurements the modal is showing
            // while it is open. The caller follows with a refreshJob for the full detail.
            .addCase(scheduleJob.pending, (state) => {
                state.operationLoading = true;
                state.operationError = null;
            })
            .addCase(scheduleJob.fulfilled, (state, action) => {
                state.operationLoading = false;
                const index = state.jobs.findIndex(j => j.uuid === action.payload.uuid);
                if (index !== -1) state.jobs[index] = action.payload;
                if (state.selectedJob?.uuid === action.payload.uuid) {
                    state.selectedJob = { ...state.selectedJob, ...action.payload };
                }
                state.operationSuccess = 'Job booked in';
            })
            .addCase(scheduleJob.rejected, (state, action) => {
                state.operationLoading = false;
                state.operationError = action.payload;
            })

            .addCase(markJobVehicleReceived.pending, (state) => {
                state.operationLoading = true;
                state.operationError = null;
            })
            .addCase(markJobVehicleReceived.fulfilled, (state, action) => {
                state.operationLoading = false;
                const index = state.jobs.findIndex(j => j.uuid === action.payload.uuid);
                if (index !== -1) state.jobs[index] = action.payload;
                if (state.selectedJob?.uuid === action.payload.uuid) {
                    state.selectedJob = { ...state.selectedJob, ...action.payload };
                }
                state.operationSuccess = 'Vehicle received';
            })
            .addCase(markJobVehicleReceived.rejected, (state, action) => {
                state.operationLoading = false;
                state.operationError = action.payload;
            })

            // ==================== Request Job Approval (PROFILE mode) ====================
            .addCase(requestJobApproval.pending, (state) => {
                state.operationLoading = true;
                state.operationError = null;
            })
            .addCase(requestJobApproval.fulfilled, (state, action) => {
                state.operationLoading = false;
                const job = action.payload;
                const index = state.jobs.findIndex(j => j.uuid === job.uuid);
                if (index !== -1) state.jobs[index] = job;
                state.selectedJob = job;
                state.operationSuccess = 'Authorisation request sent to customer';
            })
            .addCase(requestJobApproval.rejected, (state, action) => {
                state.operationLoading = false;
                state.operationError = action.payload;
            })

            // ==================== Assign Staff ====================
            .addCase(assignStaff.pending, (state) => {
                state.operationLoading = true;
                state.operationError = null;
            })
            .addCase(assignStaff.fulfilled, (state, action) => {
                state.operationLoading = false;
                const index = state.jobs.findIndex(j => j.uuid === action.payload.uuid);
                if (index !== -1) {
                    state.jobs[index] = action.payload;
                }
                state.showAssignModal = false;
                state.selectedJob = null;
                state.operationSuccess = 'Staff assigned';
            })
            .addCase(assignStaff.rejected, (state, action) => {
                state.operationLoading = false;
                state.operationError = action.payload;
            })

            // ==================== Unassign Staff ====================
            .addCase(unassignStaff.pending, (state) => {
                state.operationLoading = true;
                state.operationError = null;
            })
            .addCase(unassignStaff.fulfilled, (state, action) => {
                state.operationLoading = false;
                const index = state.jobs.findIndex(j => j.uuid === action.payload.uuid);
                if (index !== -1) {
                    state.jobs[index] = action.payload;
                }
                if (state.selectedJob?.uuid === action.payload.uuid) {
                    state.selectedJob = { ...state.selectedJob, ...action.payload };
                }
                state.showAssignModal = false;
                state.operationSuccess = 'Staff unassigned successfully';
            })
            .addCase(unassignStaff.rejected, (state, action) => {
                state.operationLoading = false;
                state.operationError = action.payload;
            })

            // ==================== Add Work Item ====================
            .addCase(addWorkItem.pending, (state) => {
                state.operationLoading = true;
                state.operationError = null;
            })
            .addCase(addWorkItem.fulfilled, (state, action) => {
                state.operationLoading = false;
                const index = state.jobs.findIndex(j => j.uuid === action.payload.uuid);
                if (index !== -1) {
                    state.jobs[index] = action.payload;
                }
                state.selectedJob = action.payload;
                state.showWorkItemModal = false;
                const awaitingConfirmation = action.payload.workItems?.some(
                    wi => wi.status === 'AWAITING_CONFIRMATION'
                );
                state.operationSuccess = awaitingConfirmation
                    ? 'Work item added — confirmation email sent to customer'
                    : 'Work item added';
            })
            .addCase(addWorkItem.rejected, (state, action) => {
                state.operationLoading = false;
                state.operationError = action.payload;
            })

            // ==================== Update Work Item ====================
            .addCase(updateWorkItem.pending, (state) => {
                state.operationLoading = true;
                state.operationError = null;
            })
            .addCase(updateWorkItem.fulfilled, (state, action) => {
                state.operationLoading = false;
                const index = state.jobs.findIndex(j => j.uuid === action.payload.uuid);
                if (index !== -1) {
                    state.jobs[index] = action.payload;
                }
                state.selectedJob = action.payload;
                state.showEditWorkItemModal = false;
                state.editingWorkItem = null;
                const editedItem = action.payload.workItems?.find(
                    wi => wi.uuid === action.meta?.arg?.workItemUuid
                );
                const justSentConfirmation = editedItem?.status === 'AWAITING_CONFIRMATION'
                    && editedItem?.customerConfirmed == null;
                state.operationSuccess = justSentConfirmation
                    ? 'Work item updated — confirmation email sent to customer'
                    : 'Work item updated';
            })
            .addCase(updateWorkItem.rejected, (state, action) => {
                state.operationLoading = false;
                state.operationError = action.payload;
            })

            // ==================== Update Work Item Status ====================
            .addCase(updateWorkItemStatus.pending, (state) => {
                state.operationLoading = true;
                state.operationError = null;
            })
            .addCase(updateWorkItemStatus.fulfilled, (state, action) => {
                state.operationLoading = false;
                const index = state.jobs.findIndex(j => j.uuid === action.payload.uuid);
                if (index !== -1) {
                    state.jobs[index] = action.payload;
                }
                state.selectedJob = action.payload;
                state.operationSuccess = 'Work item status updated';
            })
            .addCase(updateWorkItemStatus.rejected, (state, action) => {
                state.operationLoading = false;
                state.operationError = action.payload;
            })

            // ==================== Remove Work Item ====================
            .addCase(removeWorkItem.pending, (state) => {
                state.operationLoading = true;
                state.operationError = null;
            })
            .addCase(removeWorkItem.fulfilled, (state, action) => {
                state.operationLoading = false;
                const index = state.jobs.findIndex(j => j.uuid === action.payload.uuid);
                if (index !== -1) {
                    state.jobs[index] = action.payload;
                }
                state.selectedJob = action.payload;
                state.operationSuccess = 'Work item removed';
            })
            .addCase(removeWorkItem.rejected, (state, action) => {
                state.operationLoading = false;
                state.operationError = action.payload;
            })

            // ==================== Bulk Start All Work Items ====================
            .addCase(startAllWorkItems.pending, (state) => {
                state.operationLoading = true;
                state.operationError = null;
            })
            .addCase(startAllWorkItems.fulfilled, (state, action) => {
                state.operationLoading = false;
                const index = state.jobs.findIndex(j => j.uuid === action.payload.uuid);
                if (index !== -1) {
                    state.jobs[index] = action.payload;
                }
                state.selectedJob = action.payload;
                state.operationSuccess = 'All work items started';
            })
            .addCase(startAllWorkItems.rejected, (state, action) => {
                state.operationLoading = false;
                state.operationError = action.payload;
            })

            // ==================== Bulk Complete All Work Items ====================
            .addCase(completeAllWorkItems.pending, (state) => {
                state.operationLoading = true;
                state.operationError = null;
            })
            .addCase(completeAllWorkItems.fulfilled, (state, action) => {
                state.operationLoading = false;
                const index = state.jobs.findIndex(j => j.uuid === action.payload.uuid);
                if (index !== -1) {
                    state.jobs[index] = action.payload;
                }
                state.selectedJob = action.payload;
                state.operationSuccess = 'All work items completed';
            })
            .addCase(completeAllWorkItems.rejected, (state, action) => {
                state.operationLoading = false;
                state.operationError = action.payload;
            })

            // ==================== Record Observation ====================
            .addCase(recordObservation.fulfilled, (state, action) => {
                const job = action.payload;
                const index = state.jobs.findIndex(j => j.uuid === job.uuid);
                if (index !== -1) state.jobs[index] = job;
                state.selectedJob = job;
            })
            .addCase(recordObservation.rejected, (state, action) => {
                state.operationError = action.payload;
            })

            // Bulk variant — same handling; the payload is the same re-fetched job.
            .addCase(recordObservations.fulfilled, (state, action) => {
                const job = action.payload;
                const index = state.jobs.findIndex(j => j.uuid === job.uuid);
                if (index !== -1) state.jobs[index] = job;
                state.selectedJob = job;
            })
            .addCase(recordObservations.rejected, (state, action) => {
                state.operationError = action.payload;
            })

            // ==================== Link Measurement (ad-hoc) ====================
            .addCase(linkMeasurement.fulfilled, (state, action) => {
                const job = action.payload;
                const index = state.jobs.findIndex(j => j.uuid === job.uuid);
                if (index !== -1) state.jobs[index] = job;
                state.selectedJob = job;
                state.operationSuccess = 'Measurement added';
            })
            .addCase(linkMeasurement.rejected, (state, action) => {
                state.operationError = action.payload;
            })

            // ==================== Unlink Measurement (ad-hoc) ====================
            .addCase(unlinkMeasurement.fulfilled, (state, action) => {
                const job = action.payload;
                const index = state.jobs.findIndex(j => j.uuid === job.uuid);
                if (index !== -1) state.jobs[index] = job;
                state.selectedJob = job;
                state.operationSuccess = 'Measurement removed';
            })
            .addCase(unlinkMeasurement.rejected, (state, action) => {
                state.operationError = action.payload;
            })

            // ==================== Fetch Available Measurements (catalogue) ====================
            .addCase(fetchAvailableMeasurements.pending, (state) => {
                state.availableMeasurementsLoading = true;
                state.availableMeasurementsError = null;
            })
            .addCase(fetchAvailableMeasurements.fulfilled, (state, action) => {
                state.availableMeasurementsLoading = false;
                state.availableMeasurementsLoaded = true;
                state.availableMeasurements = action.payload;
            })
            .addCase(fetchAvailableMeasurements.rejected, (state, action) => {
                state.availableMeasurementsLoading = false;
                state.availableMeasurementsError = action.payload;
            })

            // ==================== Add Note ====================
            .addCase(addJobNote.pending, (state) => {
                state.operationLoading = true;
                state.operationError = null;
            })
            .addCase(addJobNote.fulfilled, (state, action) => {
                state.operationLoading = false;
                state.operationSuccess = 'Note added';
                if (action.payload) {
                    const index = state.jobs.findIndex(j => j.uuid === action.payload.uuid);
                    if (index !== -1) {
                        state.jobs[index] = action.payload;
                    }
                    state.selectedJob = action.payload;
                }
            })
            .addCase(addJobNote.rejected, (state, action) => {
                state.operationLoading = false;
                state.operationError = action.payload;
            })

            // ==================== Fetch Job Resources ====================
            .addCase(fetchJobResources.pending, (state) => {
                state.resourcesLoading = true;
                state.resourcesError = null;
            })
            .addCase(fetchJobResources.fulfilled, (state, action) => {
                state.resourcesLoading = false;
                state.jobResources = action.payload;
            })
            .addCase(fetchJobResources.rejected, (state, action) => {
                state.resourcesLoading = false;
                state.resourcesError = action.payload;
            })

            // ==================== Fetch Available Resources ====================
            .addCase(fetchAvailableResources.pending, (state) => {
                state.resourcesLoading = true;
                state.resourcesError = null;
            })
            .addCase(fetchAvailableResources.fulfilled, (state, action) => {
                state.resourcesLoading = false;
                state.availableResources = action.payload;
            })
            .addCase(fetchAvailableResources.rejected, (state, action) => {
                state.resourcesLoading = false;
                state.resourcesError = action.payload;
            })

            // ==================== Assign Resource ====================
            .addCase(assignResource.pending, (state) => {
                state.operationLoading = true;
                state.operationError = null;
            })
            .addCase(assignResource.fulfilled, (state) => {
                state.operationLoading = false;
                state.operationSuccess = 'Resource assigned';
            })
            .addCase(assignResource.rejected, (state, action) => {
                state.operationLoading = false;
                state.operationError = action.payload;
            })

            // ==================== Unassign Resource ====================
            .addCase(unassignResource.pending, (state) => {
                state.operationLoading = true;
                state.operationError = null;
            })
            .addCase(unassignResource.fulfilled, (state) => {
                state.operationLoading = false;
                state.operationSuccess = 'Resource released';
            })
            .addCase(unassignResource.rejected, (state, action) => {
                state.operationLoading = false;
                state.operationError = action.payload;
            })

            // 🔑 Keep the Queued column's own list in step with the lifecycle. Every
            // case above writes the returned job into `state.jobs`; a job that STARTS
            // from the Queued column has to leave that column too, or it sits there
            // until the next refresh looking like it never started.
            .addMatcher(
                isAnyOf(updateJobStatus.fulfilled, startJob.fulfilled, pauseJob.fulfilled,
                    resumeJob.fulfilled, completeJob.fulfilled, cancelJob.fulfilled,
                    assignStaff.fulfilled, unassignStaff.fulfilled, vehicleCollected.fulfilled),
                (state, action) => { syncQueued(state, action.payload); },
            )
            .addMatcher(isAnyOf(purgeJob.fulfilled), (state, action) => {
                state.queued.jobs = state.queued.jobs.filter(j => j.uuid !== action.payload.uuid);
            });
    },
});

/**
 * Which statuses the Queued column holds. The server owns this rule for the QUERY;
 * this copy only decides whether a job the server just handed back stays in the list
 * it is already in, until the next fetch replaces the whole page anyway.
 */
export const QUEUED_STATUSES = ['CREATED', 'QUOTED'];

/** A job the server handed back: keep it in the Queued list if still queued, else drop it. */
function syncQueued(state, job) {
    if (!job?.uuid) return;
    const index = state.queued.jobs.findIndex(j => j.uuid === job.uuid);
    if (index === -1) return;
    if (QUEUED_STATUSES.includes(job.status)) {
        state.queued.jobs[index] = job;
    } else {
        state.queued.jobs.splice(index, 1);
        state.queued.total = Math.max(0, state.queued.total - 1);
    }
}

/** The same job in every list that holds it — the month list, the queued page, the open modal. */
function replaceEverywhere(state, job) {
    if (!job?.uuid) return;
    const i = state.jobs.findIndex(j => j.uuid === job.uuid);
    if (i !== -1) state.jobs[i] = job;
    const q = state.queued.jobs.findIndex(j => j.uuid === job.uuid);
    if (q !== -1) state.queued.jobs[q] = job;
    if (state.selectedJob?.uuid === job.uuid) state.selectedJob = { ...state.selectedJob, ...job };
}

// ============================================
// Selectors
// ============================================

export const selectJobsGroupedByStatus = (state) => {
    const grouped = {
        CREATED: [],
        QUOTED: [],
        APPROVED: [],
        IN_PROGRESS: [],
        PAUSED: [],
        COMPLETED: [],
        INVOICED: [],
        CANCELLED: [],
        VEHICLE_COLLECTED: [],
    };
    state.jobs.jobs.forEach(job => {
        if (grouped[job.status]) {
            grouped[job.status].push(job);
        }
    });
    return grouped;
};

export const selectKanbanColumns = (state) => {
    const jobs = state.jobs.jobs;
    return {
        queued: jobs.filter(j => j.status === 'CREATED' || j.status === 'QUOTED' || j.status === 'APPROVED'),
        inProgress: jobs.filter(j => j.status === 'IN_PROGRESS'),
        paused: jobs.filter(j => j.status === 'PAUSED'),
        completed: jobs.filter(j => j.status === 'COMPLETED'),
    };
};

export const selectJobStats = (state) => {
    const jobs = state.jobs.jobs;
    const totalActive = jobs.filter(j =>
        !['COMPLETED', 'INVOICED', 'CANCELLED', 'VEHICLE_COLLECTED'].includes(j.status)
    ).length;
    const inProgress = jobs.filter(j => j.status === 'IN_PROGRESS').length;
    const paused = jobs.filter(j => j.status === 'PAUSED').length;
    const unassigned = jobs.filter(j => !j.assignedStaffUuid && !['COMPLETED', 'INVOICED', 'CANCELLED', 'VEHICLE_COLLECTED'].includes(j.status)).length;
    const totalEstimated = jobs.reduce((sum, j) => sum + (j.totalEstimatedCost || 0), 0);
    const totalActual = jobs.reduce((sum, j) => sum + (j.totalActualCost || 0), 0);
    return { totalActive, inProgress, paused, unassigned, totalEstimated, totalActual };
};

export const selectFilteredJobs = (state) => {
    const { jobs, searchQuery, statusFilter, staffFilter } = state.jobs;
    let filtered = [...jobs];
    if (statusFilter && statusFilter !== 'all') {
        filtered = filtered.filter(job => job.status === statusFilter);
    }
    if (staffFilter) {
        filtered = filtered.filter(job => job.assignedStaffUuid === staffFilter);
    }
    if (searchQuery) {
        const query = searchQuery.toLowerCase();
        // job.description is now a structured object { serviceTypes: [...], freeText }.
        // Flatten it to a single haystack string for substring search so users can match
        // on service names ("SUSPENSION"), per-row notes ("leaky"), or the free-text field.
        // Falls back to the raw value when it is still a string (legacy / stale cache).
        const descriptionHaystack = (desc) => {
            if (!desc) return '';
            if (typeof desc === 'string') return desc;
            const parts = [];
            if (Array.isArray(desc.serviceTypes)) {
                for (const e of desc.serviceTypes) {
                    if (e?.serviceName) parts.push(e.serviceName);
                    if (e?.description) parts.push(e.description);
                }
            }
            if (desc.freeText) parts.push(desc.freeText);
            return parts.join(' ');
        };
        filtered = filtered.filter(job =>
            job.jobNumber?.toLowerCase().includes(query) ||
            job.customerName?.toLowerCase().includes(query) ||
            job.vehicleDescription?.toLowerCase().includes(query) ||
            job.vehicleRego?.toLowerCase().includes(query) ||
            descriptionHaystack(job.description).toLowerCase().includes(query)
        );
    }
    filtered.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
    return filtered;
};

// ============================================
// Exports
// ============================================

export const {
    setStatusFilter,
    setSearchQuery,
    setStaffFilter,
    clearFilters,
    setPage,
    setPageSize,
    openCreateModal,
    closeCreateModal,
    openCreateFromBookingModal,
    closeCreateFromBookingModal,
    openEditModal,
    closeEditModal,
    openViewModal,
    closeViewModal,
    openAssignModal,
    closeAssignModal,
    openWorkItemModal,
    closeWorkItemModal,
    openEditWorkItemModal,
    closeEditWorkItemModal,
    openNoteModal,
    closeNoteModal,
    openCancelConfirm,
    closeCancelConfirm,
    openDeleteConfirm,
    closeDeleteConfirm,
    openCompleteConfirm,
    closeCompleteConfirm,
    openVehicleCollectedConfirm,
    closeVehicleCollectedConfirm,
    openLoanCarReturnModal,
    closeLoanCarReturnModal,
    selectJob,
    clearSelectedJob,
    clearOperationStatus,
    setOperationSuccess,
} = jobSlice.actions;

export default jobSlice.reducer;